import { useEffect } from 'react'

const SCROLLBAR_TARGET_ATTR = 'data-scrollbar-auto-hide-target'
const SCROLLBAR_ACTIVE_ATTR = 'data-scrollbar-auto-hide-active'
const LEGACY_SCROLLBAR_TARGET_CLASS = 'scrollbar-auto-hide-target'
const LEGACY_SCROLLBAR_ACTIVE_CLASS = 'scrollbar-auto-hide-active'
const SCROLLBAR_IDLE_HIDE_DELAY_MS = 500

function getScrollElement(target: EventTarget | null): Element {
  if (target instanceof Element) {
    return target === document.body ? document.documentElement : target
  }
  if (target instanceof Document) {
    return target.scrollingElement ?? document.documentElement
  }
  return document.scrollingElement ?? document.documentElement
}

function isDocumentScrollElement(element: Element): boolean {
  return element === document.documentElement || element === document.body
}

function canScrollVertically(element: Element): boolean {
  const style = window.getComputedStyle(element)
  const overflowY = style.overflowY
  const blocksVerticalScroll = overflowY === 'hidden' || overflowY === 'clip'
  if (isDocumentScrollElement(element)) {
    return !blocksVerticalScroll && element.scrollHeight > element.clientHeight + 1
  }
  const allowsVerticalScroll =
    overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
  return allowsVerticalScroll && element.scrollHeight > element.clientHeight + 1
}

export function AutoHideScrollbars() {
  useEffect(() => {
    const targets = new Set<Element>()
    const timers = new Map<Element, number>()
    let scanFrame: number | null = null

    const hideScrollbar = (element: Element) => {
      element.removeAttribute(SCROLLBAR_ACTIVE_ATTR)
      element.classList.remove(LEGACY_SCROLLBAR_ACTIVE_CLASS)
      timers.delete(element)
    }

    const scheduleHideScrollbar = (element: Element, resetExistingTimer = true) => {
      const previousTimer = timers.get(element)
      if (previousTimer !== undefined) {
        if (!resetExistingTimer) return
        window.clearTimeout(previousTimer)
      }
      timers.set(
        element,
        window.setTimeout(() => hideScrollbar(element), SCROLLBAR_IDLE_HIDE_DELAY_MS),
      )
    }

    const markTarget = (element: Element) => {
      if (targets.has(element)) return
      element.setAttribute(SCROLLBAR_TARGET_ATTR, 'true')
      element.classList.remove(LEGACY_SCROLLBAR_TARGET_CLASS)
      targets.add(element)
    }

    const unmarkTarget = (element: Element) => {
      const timer = timers.get(element)
      if (timer !== undefined) {
        window.clearTimeout(timer)
        timers.delete(element)
      }
      element.removeAttribute(SCROLLBAR_TARGET_ATTR)
      element.removeAttribute(SCROLLBAR_ACTIVE_ATTR)
      element.classList.remove(LEGACY_SCROLLBAR_TARGET_CLASS, LEGACY_SCROLLBAR_ACTIVE_CLASS)
      targets.delete(element)
    }

    const scanScrollTargets = () => {
      scanFrame = null
      const nextTargets = new Set<Element>()
      const root = document.scrollingElement ?? document.documentElement
      if (canScrollVertically(root)) {
        nextTargets.add(root)
        markTarget(root)
      }

      document.querySelectorAll('body *').forEach((element) => {
        if (!canScrollVertically(element)) return
        nextTargets.add(element)
        markTarget(element)
      })

      targets.forEach((element) => {
        if (!nextTargets.has(element)) {
          unmarkTarget(element)
        } else if (element.hasAttribute(SCROLLBAR_ACTIVE_ATTR)) {
          scheduleHideScrollbar(element, false)
        }
      })
    }

    const scheduleScan = () => {
      if (scanFrame !== null) return
      scanFrame = window.requestAnimationFrame(scanScrollTargets)
    }

    const revealScrollbar = (element: Element) => {
      if (!canScrollVertically(element)) return
      markTarget(element)
      element.setAttribute(SCROLLBAR_ACTIVE_ATTR, 'true')
      element.classList.remove(LEGACY_SCROLLBAR_ACTIVE_CLASS)
      scheduleHideScrollbar(element)
    }

    const handleScroll = (event: Event) => {
      revealScrollbar(getScrollElement(event.target))
    }

    const observer = new MutationObserver(scheduleScan)
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
      attributeFilter: ['class', 'style'],
    })

    scheduleScan()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', scheduleScan)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', scheduleScan)
      observer.disconnect()
      if (scanFrame !== null) {
        window.cancelAnimationFrame(scanFrame)
      }
      targets.forEach(unmarkTarget)
      targets.clear()
    }
  }, [])

  return null
}
